import { DigitalCharacterInterface } from 'interfaces/digitalCharacterInterface';
import copy from 'copy-to-clipboard';
import { customAlphabet } from 'nanoid';

export const formatFileSize = (bytes: number): string => {
	if (bytes === 0) return '0 Bytes';

	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
	const i = Math.floor(Math.log(bytes) / Math.log(1024));
	const value = bytes / Math.pow(1024, i);

	return `${value.toFixed(2)} ${sizes[i]}`;
};

export const generateOtp = customAlphabet('0123456789', 6);

export const getVideoDuration = (file: File): Promise<number> => {
	return new Promise((resolve, reject) => {
		const url = URL.createObjectURL(file);
		const video = document.createElement('video');
		video.preload = 'metadata';

		video.onloadedmetadata = () => {
			URL.revokeObjectURL(url);
			const videoDuration = Math.ceil(video.duration);
			resolve(videoDuration);
		};

		video.onerror = () => {
			reject(0);
		};
		video.src = url;
	});
};

export const blobToBase64 = (blob: Blob): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onerror = reject;
		reader.onload = () => {
			resolve(reader.result?.toString().split(',')[1] || '');
		};
		reader.readAsDataURL(blob);
	});
};

export const wrapText = (text: string, maxChars: number): string => {
	const words = text.split(' ');
	let line = '';
	const lines: string[] = [];

	for (const word of words) {
		if ((line + word).length > maxChars) {
			lines.push(line.trim());
			line = word + ' ';
		} else {
			line += word + ' ';
		}
	}
	if (line) lines.push(line.trim());

	return lines.join('\n'); // FFmpeg understands \n
};

export const copyToClipboard = (text: string): boolean => {
	return copy(text);
};

export const formatDuration = (seconds: number): string => {
	if (!Number.isFinite(seconds) || seconds < 0) return '00:00';

	const hrs = Math.floor(seconds / 3600);
	const mins = Math.floor((seconds % 3600) / 60);
	const secs = Math.floor(seconds % 60);

	const parts = [
		hrs > 0 ? String(hrs).padStart(2, '0') : null,
		String(mins).padStart(2, '0'),
		String(secs).padStart(2, '0'),
	].filter(Boolean);

	return parts.join(':');
};

export const censorEmail = (email: string): string => {
	const [localPart, domain] = email.split('@');
	if (!domain) return email; // not a valid email

	// Show first and last character of local part, mask the middle
	const visibleLocal =
		localPart.length <= 2
			? localPart[0] + '*'.repeat(localPart.length - 1)
			: localPart[0] + '*'.repeat(localPart.length - 2) + localPart.slice(-1);

	// For domain, censor only the part before the first dot
	const [domainName, ...tldParts] = domain.split('.');
	const visibleDomain = domainName[0] + '*'.repeat(Math.max(1, domainName.length - 2)) + domainName.slice(-1);

	return `${visibleLocal}@${visibleDomain}.${tldParts.join('.')}`;
};

export const toSlug = (text: string): string => {
	const replacements: { [key: string]: string } = {
		ä: 'ae',
		ö: 'oe',
		ü: 'ue',
		ß: 'ss',
	};

	let processedText = text.toLowerCase();
	for (const [key, value] of Object.entries(replacements)) {
		processedText = processedText.replace(new RegExp(key, 'g'), value);
	}

	return processedText
		.trim()
		.replace(/[^\w\s-]/g, '')
		.replace(/[\s_-]+/g, '-');
};

/**
 * Defined the characters. Replaces image tags. Updates the user prompt.
 * Might be better to pass in the filesList and only convert existing media. e.g @image4 -> [Image 4] not available
 * ----------
 * First images are character definitions in order. Then assets.
 * @param prompt
 * @param availableCharacters
 */
export const convertPrompt = (prompt: string, availableCharacters: DigitalCharacterInterface[]): string => {
	const characterTagRegex = /@([a-zA-Z0-9_-]+)/g;
	const matches = prompt.matchAll(characterTagRegex);

	const uniqueKeys = new Set<string>();
	for (const match of matches) {
		uniqueKeys.add(match[1]);
	}

	const usedCharacters: DigitalCharacterInterface[] = [];
	const characterMap: { [key: string]: string } = {};
	let charCode = 65; // 'A'

	uniqueKeys.forEach((key) => {
		const char = availableCharacters.find((c) => c.key === key);
		if (char) {
			usedCharacters.push(char);
			characterMap[key] = String.fromCharCode(charCode++);
		}
	});

	if (usedCharacters.length === 0) {
		return prompt.replace(/@(image|video|audio)(\d+)/g, (match, type, index) => {
			const num = parseInt(index, 10);
			if (type === 'image') {
				return `[Image ${num}]`;
			} else if (type === 'video') {
				return `[Video ${num}]`;
			} else if (type === 'audio') {
				return `[Audio ${num}]`;
			}
			return match;
		});
	}

	let formattedPrompt = '';

	usedCharacters.forEach((char, index) => {
		const label = characterMap[char.key];
		formattedPrompt += `CHARACTER ${label}\n`;
		formattedPrompt += `Name: ${char.name}\n`;
		formattedPrompt += `Age: ${char.age}\n`;
		formattedPrompt += `Nationality / language: ${char.language}\n\n`;

		formattedPrompt += `Personality:\n`;
		formattedPrompt += `Tone: ${char.tone}\n`;
		formattedPrompt += `Humor level: ${char.humor_level}\n`;
		formattedPrompt += `Energy level: ${char.energy_level}\n`;
		formattedPrompt += `Warmth: ${char.warmth}\n`;
		formattedPrompt += `Assertiveness: ${char.assertiveness}\n`;
		formattedPrompt += `Default mood: ${char.mood}\n`;
		formattedPrompt += `Sentence length: ${char.sentence_length}\n\n`;

		if (char.behavior_constraints) {
			formattedPrompt += `BEHAVIOR CONSTRAINTS (ALWAYS FOLLOW)\n`;
			formattedPrompt += `${char.behavior_constraints}\n\n`;
		}

		formattedPrompt += `VISUAL CHARACTER\n`;
		formattedPrompt += `Person appearance: [Image ${index + 1}]\n`;
		if (char.type === 'real') {
			formattedPrompt += `The character MUST be rendered as a real human actor\n\n`;
		}

		formattedPrompt += `---\n\n`;
	});

	let updatedSceneRequest = prompt;
	Object.entries(characterMap).forEach(([key, label]) => {
		const regex = new RegExp(`@${key}`, 'g');
		updatedSceneRequest = updatedSceneRequest.replace(regex, `CHARACTER ${label}`);
	});

	// Replace @imageN, @videoN, @audioN
	const imageOffset = usedCharacters.length;
	updatedSceneRequest = updatedSceneRequest.replace(/@(image|video|audio)(\d+)/g, (match, type, index) => {
		const num = parseInt(index, 10);
		if (type === 'image') {
			return `[Image ${num + imageOffset}]`;
		} else if (type === 'video') {
			return `[Video ${num}]`;
		} else if (type === 'audio') {
			return `[Audio ${num}]`;
		}
		return match;
	});

	formattedPrompt += `SCENE REQUEST:\n${updatedSceneRequest}`;

	return formattedPrompt.trim();
};
